import { useEffect } from 'react'

import { useLazyQuery, useMutation } from '@apollo/client'
import { IconTrash } from '@tabler/icons-react'
import { message, Form, Input } from 'antd'

import { Button } from '../BUI/Button'
import { Modal } from '../BUI/Modal'

import { FolderData } from '@/hooks/useDocumentFolders'
import {
  DELETE_DOCUMENT_FOLDER,
  GET_DOCUMENTS_LIST_WITH_FOLDERS,
  IS_DOCUMENT_FOLDER_EMPTY,
  UPDATE_DOCUMENT_FOLDER,
} from '@/modules/documents/graphQL'

import {
  DeleteDocumentFolderUiMutation,
  DeleteDocumentFolderUiMutationVariables,
  IsDocumentFolderEmptyUiQuery,
  IsDocumentFolderEmptyUiQueryVariables,
  UpdateDocumentFolderUiMutation,
  UpdateDocumentFolderUiMutationVariables,
} from 'types/api.graphql'

type Props = {
  folder: FolderData
  open: boolean
  onClose: () => void
  onUpdate?: () => void
}

export const UpdateDocumentFolder: React.FC<Props> = ({
  folder,
  open,
  onClose,
  onUpdate,
}) => {
  const [isEmpty, { data: emptyData, loading: checkingIfEmpty }] = useLazyQuery<
    IsDocumentFolderEmptyUiQuery,
    IsDocumentFolderEmptyUiQueryVariables
  >(IS_DOCUMENT_FOLDER_EMPTY, { fetchPolicy: 'network-only' })

  const [updateFolder, { loading }] = useMutation<
    UpdateDocumentFolderUiMutation,
    UpdateDocumentFolderUiMutationVariables
  >(UPDATE_DOCUMENT_FOLDER)

  const [deleteFolder, { loading: deleting }] = useMutation<
    DeleteDocumentFolderUiMutation,
    DeleteDocumentFolderUiMutationVariables
  >(DELETE_DOCUMENT_FOLDER, {
    refetchQueries: [GET_DOCUMENTS_LIST_WITH_FOLDERS],
  })

  useEffect(() => {
    if (open) {
      isEmpty({ variables: { id: folder.folderId } })
    }
  }, [open, folder.folderId, isEmpty])

  const onSubmit = async ({ name }) => {
    await updateFolder({ variables: { id: folder.folderId, input: { name } } })

    message.success(`Folder "${name}" updated successfully`)

    onClose()
    onUpdate?.()
  }

  const onDeleteFolder = async () => {
    deleteFolder({ variables: { id: folder.folderId } })

    onClose()
  }

  return (
    <Modal
      destroyOnClose
      open={open}
      onCancel={onClose}
      title="Update folder"
      footerLayout="split"
      footer={[
        <Button
          key="delete"
          negative
          onClick={onDeleteFolder}
          loading={checkingIfEmpty || deleting}
          disabled={!emptyData?.isDocumentFolderEmpty}
          icon={<IconTrash size={16} />}
        >
          Delete {!emptyData?.isDocumentFolderEmpty && ' (not empty)'}
        </Button>,
        <Button key="update" variant="primary" type="submit" loading={loading}>
          Update
        </Button>,
      ]}
      modalRender={(children) => (
        <Form
          layout="vertical"
          onFinish={onSubmit}
          initialValues={{ name: folder.folderName }}
        >
          {children}
        </Form>
      )}
    >
      <Form.Item
        name="name"
        label="Folder name"
        rules={[{ required: true, message: 'Please provide a folder name' }]}
      >
        <Input placeholder="Health & safety" />
      </Form.Item>
    </Modal>
  )
}
